import type { Tour } from "@getaways/core";
import type { CatalogueImportResult } from "@getaways/tours";
import { api, Badge, Banner, Checkbox, Empty, Field, PageHead, Panel, useAsync, type Notice } from "@getaways/extranet-ui";
import { IconSearch } from "@tabler/icons-react";
import { useState } from "react";
import { useCan } from "../lib/access";

type Row = Tour & { curated: boolean; hidden: boolean; supplierName: string; importedAt: string };

/** Tours with our own name, text and photos on top of the supplier's; hidden tours stay out of search. */
export function CataloguePage() {
  const can = useCan();
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const { data, error, reload } = useAsync(() => api<{ tours: Row[]; total: number }>("GET", `/v1/admin/catalogue?limit=500${q.length >= 2 ? `&q=${encodeURIComponent(q)}` : ""}`), [q]);
  const [notice, setNotice] = useState<Notice>();
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState<{ tour: Row; name: string; description: string; images: string; hidden: boolean }>();

  const importNow = async () => {
    setBusy(true);
    try {
      const r = await api<CatalogueImportResult>("POST", "/v1/admin/catalogue/import");
      setNotice({ tone: "ok", text: `${r.tours} tours imported, ${r.withdrawn} withdrawn.` });
      reload();
    } catch (err) {
      setNotice({ tone: "bad", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!edit) return;
    const images = edit.images.split("\n").map((s) => s.trim()).filter(Boolean).map((url) => ({ url, kind: "photo" as const }));
    await api("PUT", `/v1/admin/catalogue/${edit.tour.supplier}/${encodeURIComponent(edit.tour.code)}`, { ...(edit.name.trim() ? { name: edit.name.trim() } : {}), description: edit.description, images, hidden: edit.hidden });
    setEdit(undefined);
    reload();
  };

  return (
    <>
      <PageHead title="Catalogue" sub="Imported every night from the suppliers. Departures and prices are always read live; our text and photos are never overwritten.">
        {can("catalogue:manage") && <button disabled={busy} onClick={() => void importNow()}>{busy ? "Importing…" : "Import now"}</button>}
      </PageHead>
      {(error || notice) && <Banner notice={notice ?? { tone: "bad", text: error! }} onClose={() => setNotice(undefined)} />}
      <div className="card" style={{ padding: 0 }}>
        <div className="row" style={{ padding: "12px 14px", borderBottom: "1px solid var(--line)" }}>
          <span className="small muted">{data ? `${data.total} tours` : ""}</span>
          <span className="spacer" />
          <form style={{ position: "relative", width: 260 }} onSubmit={(e) => { e.preventDefault(); setQ(text.trim()); }}>
            <IconSearch size={16} style={{ position: "absolute", left: 10, top: 9, color: "var(--faint)" }} aria-hidden />
            <input value={text} onChange={(e) => setText(e.target.value)} onBlur={() => setQ(text.trim())} placeholder="Name, code, country" style={{ paddingLeft: 32 }} aria-label="Search tours" />
          </form>
        </div>
        {data && data.tours.length === 0 ? <Empty>No tours yet.</Empty> : (
          <table className="table">
            <thead><tr><th>Tour</th><th>Where</th><th>Days</th><th>Our content</th><th /></tr></thead>
            <tbody>
              {(data?.tours ?? []).map((t) => (
                <tr key={`${t.supplier}:${t.code}`}>
                  <td>{t.name}{t.name !== t.supplierName && <div className="small muted">{t.supplierName}</div>}<div className="small muted mono">{t.supplier} · {t.code}{t.productCode ? ` · ${t.productCode}` : ""}</div></td>
                  <td className="small">{t.region ?? "—"}<div className="muted">{t.countries.join(", ")}</div></td>
                  <td className="small">{t.durationDays ?? "—"}</td>
                  <td className="small">{t.curated ? "curated" : <span className="muted">supplier's</span>}{t.hidden && <> <Badge>hidden</Badge></>}</td>
                  <td>{can("catalogue:manage") && <button onClick={() => setEdit({ tour: t, name: t.curated && t.name !== t.supplierName ? t.name : "", description: t.description ?? "", images: t.images.filter((i) => i.kind === "photo").map((i) => i.url).join("\n"), hidden: t.hidden })}>Edit</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {edit && (
        <Panel title={edit.tour.supplierName} sub={`${edit.tour.supplier} · ${edit.tour.code}`} onClose={() => setEdit(undefined)} actions={<><button className="primary" onClick={() => void save()}>Save</button><button onClick={() => setEdit(undefined)}>Cancel</button></>}>
          <Field label="Our name" help="Empty: the supplier's"><input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder={edit.tour.supplierName} /></Field>
          <Field label="Description"><textarea rows={8} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></Field>
          <Field label="Photos" help="One https URL per line, in display order"><textarea rows={6} value={edit.images} onChange={(e) => setEdit({ ...edit, images: e.target.value })} /></Field>
          <Checkbox checked={edit.hidden} onCheckedChange={(hidden) => setEdit({ ...edit, hidden })} label="Hide from search" />
        </Panel>
      )}
    </>
  );
}
